import React, { useEffect, useState } from 'react';
import { Radio, Select, Title } from '@meitu/saas-ui';
import { editor } from '@/instance';
import './image-config-panel.less';
import { cloneDeep, isUndefined } from 'lodash';
import { defaultImageTransform } from '@meitu/saas-editor-core/es/layers/image-layer/constants/default-image-data';
import { createCanvas, getImgInfoAsync, loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { computerBgRatio } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import {
    IImageLayerProps,
    ILayerProps,
    ImgCategoryType,
    ImgModeType,
    LayerType,
    StretchOptionType,
} from '@meitu/saas-editor-core/es/version-types';
import { uploadBase64ToObs } from '@meitu/xdesign-widgets';
import { useLocaleStore } from '@/i18n';
import { hintOptions, imgModeOptions, IOptionProp, shineOptions, stretchOptions, tagBgImgConfig } from '../type';

const { CustomTitle } = Title;

enum ImgMode {
    none = 0,
    imgContainer = 1,
}
const tagConfigOptions: IOptionProp[] = [
    {
        label: '无标签',
        value: ImgCategoryType.none,
    },
    {
        label: '素材',
        value: ImgCategoryType.material,
    },
    {
        label: '二维码',
        value: ImgCategoryType.qrCode,
    },
    {
        label: '主图',
        value: ImgCategoryType.mainImg,
    },
    {
        label: '辅图',
        value: ImgCategoryType.subImg,
    },
    {
        label: '正面',
        value: ImgCategoryType.frontImg,
    },
    {
        label: '反面',
        value: ImgCategoryType.backImg,
    },
];

export const TagAllList = tagConfigOptions.concat(tagBgImgConfig);

const categoryMap: any = {};
TagAllList.forEach((item) => {
    categoryMap[item.value] = item;
});

const modeMap: any = {};
imgModeOptions.forEach((item) => {
    modeMap[item.value] = item;
});

const ImageConfigPanelRender: React.FC<{ layerId: string }> = ({ layerId }) => {
    const { $t } = useLocaleStore();
    const activeLayer = editor.services.flatLayer.getLayer(layerId);
    const layers = editor.services.frame.getLayers(editor.services.frame.getActiveFrameId());
    const [tag, setTag] = useState(tagConfigOptions[0]);
    const [imgMode, setImgMode] = useState(imgModeOptions[0]);
    const [stretch, setStretch] = useState(stretchOptions[0]);
    const [shine, setShine] = useState(shineOptions[0]);
    const [hint, setHint] = useState(hintOptions[1].value);
    /** 是否图片容器 */
    const isImgContainer = imgMode.value === ImgMode.imgContainer;
    const [tagOptions, setTagOptions] = useState<IOptionProp[]>([]);
    const [mainImgId, setMainImgId] = useState<string>('');

    const updateTag = (layerId: string, category: ImgCategoryType) => {
        try {
            const layer = editor.services.flatLayer.getLayer(layerId);
            const newLayer = cloneDeep(layer);

            if (!newLayer) return;
            newLayer.category = category;
            if (category === ImgCategoryType.bgImg) {
                editor.imageLayer.convertImageToBackground(layerId, true, layer.materialIds);
                return;
            }
            if (category === ImgCategoryType.qrCode) {
                (newLayer.data as IImageLayerProps).stretchOption = StretchOptionType.fill;
                newLayer.shine = false;
                newLayer.mode = ImgModeType.none;
            }
            // update
            editor.imageLayer.updateLayer(newLayer);
        } catch (e) {
            console.error(e);
        }
    };

    const updateMode = async (layerId: string, mode: ImgModeType) => {
        try {
            const layer = editor.services.flatLayer.getLayer(layerId);
            const newLayer = cloneDeep(layer);

            if (!newLayer) return;
            const { width, height } = newLayer;
            const data = newLayer.data as IImageLayerProps;
            newLayer.mode = mode;
            if (mode === ImgModeType.imgContainer) {
                if (data?.url) {
                    const baseImage = await loadImageAsync(data.url);
                    const { canvas } = createCanvas(newLayer.width, newLayer.height, baseImage);
                    const resultBase64 = canvas.toDataURL();
                    const { url } = await uploadBase64ToObs(resultBase64);

                    data.maskURL = url;
                    const defaultMaskImage = editor.imageLayer.getDefaultMaskImage();
                    data.url = defaultMaskImage.url;
                    newLayer.shine = true;
                    newLayer.category = ImgCategoryType.material;
                    data.stretchOption = StretchOptionType.cover;
                    data.naturalWidth = defaultMaskImage.width;
                    data.naturalHeight = defaultMaskImage.height;
                    data.imageTransform = {
                        ...cloneDeep(defaultImageTransform),
                        ...computerBgRatio({
                            imgWidth: defaultMaskImage.width,
                            imgHeight: defaultMaskImage.height,
                            stageHeight: height,
                            stageWidth: width,
                        }),
                    };
                }
            } else if (data?.url && data?.maskURL) {
                if (data.url === editor.imageLayer.getDefaultMaskImage().url) {
                    const imgData = await getImgInfoAsync(data.maskURL);

                    data.maskURL = '';
                    data.url = data.maskURL;
                    const { width: imgWidth, height: imgHeight } = imgData;
                    data.naturalWidth = imgWidth;
                    data.naturalHeight = imgHeight;
                    data.imageTransform = {
                        ...cloneDeep(defaultImageTransform),
                        ...computerBgRatio({
                            imgWidth,
                            imgHeight,
                            stageHeight: height,
                            stageWidth: width,
                        }),
                    };
                } else {
                    data.maskURL = '';
                }
            }

            // update
            editor.imageLayer.updateLayer(newLayer, layer);
        } catch (e) {
            console.error(e);
        }
    };

    const updateStretch = (layerId: string, stretchOption: StretchOptionType) => {
        try {
            const layer = editor.services.flatLayer.getLayer(layerId);
            const newLayer = cloneDeep(layer);
            if (!newLayer) return;
            let ratio = {
                widthRatio: 1,
                heightRatio: 1,
            };
            const { width, height } = newLayer;
            const data = newLayer.data as IImageLayerProps;
            data.stretchOption = stretchOption;
            const { naturalWidth, naturalHeight, imageTransform } = data;
            const { flipH, flipV } = imageTransform;
            if (stretchOption === StretchOptionType.cover) {
                ratio = computerBgRatio({
                    imgWidth: naturalWidth,
                    imgHeight: naturalHeight,
                    stageHeight: height,
                    stageWidth: width,
                });
            }
            data.imageTransform = { ...data.imageTransform, ...defaultImageTransform, ...ratio, flipH, flipV };
            editor.imageLayer.updateLayer(newLayer, layer);
        } catch (e) {
            console.error(e);
        }
    };

    const updateShine = (layerId: string, shine: boolean) => {
        try {
            const layer = editor.services.flatLayer.getLayer(layerId);
            const newLayer = cloneDeep(layer);
            if (!newLayer) return;
            newLayer.shine = shine;
            editor.imageLayer.updateLayer(newLayer, layer);
        } catch (e) {
            console.error(e);
        }
    };

    const updateHint = (layerId: string, hint: boolean) => {
        try {
            const layer = editor.services.flatLayer.getLayer(layerId);
            const newLayer = cloneDeep(layer);
            if (!newLayer) return;
            (newLayer.data as IImageLayerProps).hint = hint;
            editor.imageLayer.updateLayer(newLayer, layer);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        const tagOptions = cloneDeep(tagConfigOptions).map((item) => {
            if (mainImgId && activeLayer?.id !== mainImgId && item.value === ImgCategoryType.mainImg) {
                item.disabled = true;
            } else {
                item.disabled = false;
            }
            return item;
        });

        const tagBgImgOption = cloneDeep([tagBgImgConfig]).map((item) => {
            if (isImgContainer && item.value === ImgCategoryType.bgImg) {
                item.disabled = true;
            }
            return item;
        });

        if (activeLayer?.parentId) {
            setTagOptions([...tagOptions]);
        } else {
            setTagOptions([...tagOptions, ...tagBgImgOption]);
        }
    }, [activeLayer, mainImgId, isImgContainer]);

    useEffect(() => {
        if (activeLayer) {
            // 设置高亮闪烁
            if (+activeLayer.shine !== shine.value) {
                setShine(shineOptions[+activeLayer.shine]);
            }
            if (activeLayer?.data) {
                const data = activeLayer.data as IImageLayerProps;
                const stretchOption = +data.stretchOption as StretchOptionType;
                if (!isUndefined(data.stretchOption) && stretchOption !== stretch.value) {
                    setStretch(stretchOptions[stretchOption]);
                }
                if (!data.hint) {
                    setHint(0);
                } else if (+data.hint !== hint) {
                    setHint(+data.hint);
                }
            }
            if (!isUndefined(activeLayer.mode) && (activeLayer.mode as number) !== imgMode.value) {
                setImgMode(modeMap[activeLayer.mode]);
            }
            if (!isUndefined(activeLayer.category) && (activeLayer.category as number) !== tag.value) {
                setTag(categoryMap[activeLayer.category]);
            }
        }
    }, [activeLayer]);

    const initMainImgId = (update = true) => {
        if (!update) return;
        let hasMainImg = false;
        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.layerType === LayerType.IMAGE_LAYER && layer.category === ImgCategoryType.mainImg) {
                if (hasMainImg) {
                    layer.category = ImgCategoryType.material;
                    updateTag(layer.id, ImgCategoryType.material);
                    // console.log(`取消设置主图 ID ${layer!.id}`);
                } else {
                    hasMainImg = true;
                    setMainImgId(layer.id);
                    // console.log(`设置主图 ID 为 ${layer!.id}`);
                }
            }
        });
    };

    useEffect(() => {
        initMainImgId();
    }, [layers.length]);

    const handleTag = (index: number) => {
        setTag(categoryMap[index]);
        const value = categoryMap[index].value;
        if (value === tag.value) return;
        if (value === ImgCategoryType.mainImg) {
            setMainImgId(activeLayer!.id);
            // console.log(`设置主图 ID 为 ${activeLayer!.id}`);
        } else if (activeLayer!.id === mainImgId) {
            setMainImgId('');
            // console.log(`取消主图 ID`);
        }
        updateTag(activeLayer!.id, value);
    };
    const handleImgMode = (index: number) => {
        setImgMode(modeMap[index]);
        const value = modeMap[index].value;
        if (value === imgMode.value) return;
        updateMode(activeLayer!.id, value);
    };
    const handleStretch = (index: number) => {
        setStretch(stretchOptions[index]);
        const value = stretchOptions[index].value;
        if (value === stretch.value) return;
        updateStretch(activeLayer!.id, value);
    };
    const handleShine = (index: number) => {
        setShine(shineOptions[index]);
        const value = shineOptions[index].value;
        if (value === shine.value) return;
        updateShine(activeLayer!.id, !!value);
    };
    const handleHint = (value: number) => {
        setHint(value);
        if (value === hint) return;
        updateHint(activeLayer!.id, !!value);
    };

    if (!activeLayer) return null;
    return (
        <>
            <div className="c-images-panel__config">
                {/* 无标签 */}
                <Select
                    optionLabelProp="label"
                    optionFilterProp="label"
                    value={tag.value}
                    options={tagOptions}
                    onChange={handleTag}
                    onDropdownVisibleChange={initMainImgId}
                ></Select>
                {/* 无功能 */}
                <Select
                    value={imgMode.value}
                    options={imgModeOptions}
                    onChange={handleImgMode}
                    disabled={false}
                ></Select>
                {/* 拉伸 */}
                <Select
                    value={stretch.value}
                    options={stretchOptions}
                    onChange={handleStretch}
                    disabled={false}
                ></Select>
                {/* 高亮闪烁 */}
                <Select
                    disabled={isImgContainer}
                    value={shine.value}
                    options={shineOptions}
                    onChange={handleShine}
                ></Select>
            </div>
            <div style={{ padding: '0 16px' }}>
                <CustomTitle
                    leftNode={<div className="label-action__title">展示替换按钮</div>}
                    rightNode={
                        <div>
                            <Radio.Group
                                style={{ margin: 0, alignItems: 'center' }}
                                value={hint}
                                onChange={(value) => {
                                    handleHint(value.target.value);
                                }}
                                options={hintOptions}
                            ></Radio.Group>
                        </div>
                    }
                />
            </div>
        </>
    );
};
export default React.memo(ImageConfigPanelRender);
